import * as React from "react";
import {
  Box,
  Button,
  Field as ChakraField,
  HStack,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  Formik,
  Form,
  Field,
  FieldProps,
  FormikHelpers,
  FormikProps,
} from "formik";

import {
  raiseComplaintReset,
  raiseComplaintRequest,
  fetchDispositionsRequest,
} from "store/bbps/Complaint/bbpsComplaintActions";
import { RootState } from "store/rootReducer";
import { BbpsComplainLayout } from "layouts/BbpsComplainLayout/BbpsComplainLayout";
import { UniversalSelect } from "layouts/UniversalSelect/UniversalSelect";

type UniversalSelectItem = { label: string; value: string };

interface RaiseTxnComplaintFormValues {
  orderId: string;
  disposition: string; // store CODE e.g. "D11"
  description: string;
}

const Row: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => {
  if (!value) return null;
  return (
    <HStack justify="space-between">
      <Text fontSize="sm" color="gray.600">
        {label}
      </Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color="gray.800"
        textAlign="right"
      >
        {value}
      </Text>
    </HStack>
  );
};

const FloatingInputField: React.FC<
  {
    label: string;
    maxLength?: number;
    required?: boolean;
  } & FieldProps
> = ({ label, field, form, maxLength, required }) => {
  const [focus, setFocus] = React.useState(false);
  const value = String(field.value ?? "");
  const active = focus || value.length > 0;

  const error =
    (form.touched as any)?.[field.name] && (form.errors as any)?.[field.name];

  return (
    <ChakraField.Root invalid={!!error}>
      <Box position="relative">
        <Input
          {...field}
          value={value}
          maxLength={maxLength}
          onFocus={() => setFocus(true)}
          onBlur={(e) => {
            setFocus(false);
            field.onBlur(e);
          }}
          minW={"100vh"}
          h="52px"
          pt="18px"
          bg="white"
          borderColor="blackAlpha.200"
          _hover={{ borderColor: "blackAlpha.300" }}
          _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px" }}
          required={required}
          autoComplete="new-password"
        />

        <Text
          position="absolute"
          left="12px"
          top={active ? "8px" : "50%"}
          transform={active ? "translateY(0)" : "translateY(-50%)"}
          fontSize={active ? "xs" : "sm"}
          color={active ? "gray.600" : "gray.500"}
          pointerEvents="none"
          transition="all 120ms ease"
          bg="white"
          px="6px"
        >
          {label}
        </Text>
      </Box>

      {error ? (
        <ChakraField.ErrorText>{String(error)}</ChakraField.ErrorText>
      ) : null}
    </ChakraField.Root>
  );
};

export const RaiseTxnComplaintPage: React.FC = () => {
  const dispatch = useDispatch();
  const raise = useSelector((s: RootState) => s.bbpsComplaint.raise);

  const dispositionsBox = useSelector(
    (s: RootState) => s.bbpsComplaint.dispositions,
  );

  React.useEffect(() => {
    dispatch(fetchDispositionsRequest());
  }, [dispatch]);

  React.useEffect(() => {
    return () => {
      dispatch(raiseComplaintReset());
    };
  }, [dispatch]);

  const dispositionItems: UniversalSelectItem[] = React.useMemo(() => {
    const list = dispositionsBox.data ?? [];
    return list.map((d) => ({ label: d.label, value: d.code }));
  }, [dispositionsBox.data]);

  const initialValues: RaiseTxnComplaintFormValues = {
    orderId: "",
    disposition: "",
    description: "",
  };

  const validate = (values: RaiseTxnComplaintFormValues) => {
    const e: Partial<Record<keyof RaiseTxnComplaintFormValues, string>> = {};

    const orderId = values.orderId?.trim() ?? "";
    const disposition = values.disposition?.trim() ?? "";
    const description = values.description?.trim() ?? "";

    if (!orderId) e.orderId = "Transaction ID is required.";
    if (orderId.length > 30)
      e.orderId = "Transaction ID must be <= 30 characters.";

    if (!disposition) e.disposition = "Please select a reason.";

    if (!description) e.description = "Description is required.";
    if (description.length > 300)
      e.description = "Description must be <= 300 characters.";

    return e;
  };

  const handleSubmit = (
    values: RaiseTxnComplaintFormValues,
    helpers: FormikHelpers<RaiseTxnComplaintFormValues>,
  ) => {
    if (raise.loading) return;

    dispatch(raiseComplaintReset());

    dispatch(
      raiseComplaintRequest({
        orderId: values.orderId.trim(),
        disposition: values.disposition.trim(),
        description: values.description.trim(),
      }),
    );

    helpers.setSubmitting(false);
  };

  return (
    <BbpsComplainLayout title="Raise a Transaction Complaint" backTo="/bbps">
      <Formik<RaiseTxnComplaintFormValues>
        initialValues={initialValues}
        validate={validate}
        validateOnBlur
        validateOnChange
        onSubmit={handleSubmit}
      >
        {(fp: FormikProps<RaiseTxnComplaintFormValues>) => {
          const canSubmit =
            fp.isValid &&
            !raise.loading &&
            !fp.isSubmitting &&
            !!fp.values.orderId.trim() &&
            !!fp.values.disposition.trim() &&
            !!fp.values.description.trim();

          return (
            <Form>
              <VStack align="stretch" gap={4}>
                {/* Transaction ID */}
                <Field name="orderId">
                  {(p: FieldProps) => (
                    <FloatingInputField
                      {...p}
                      label="Transaction ID"
                      maxLength={30}
                      required
                    />
                  )}
                </Field>

                {/* Reason */}
                <UniversalSelect
                  id="disposition"
                  label="Reason for raising the concern"
                  placeholder={
                    dispositionsBox.loading ? "Loading..." : "-- choose --"
                  }
                  items={dispositionItems}
                  multiple={false}
                  value={fp.values.disposition}
                  loading={dispositionsBox.loading}
                  disabled={
                    raise.loading || fp.isSubmitting || dispositionsBox.loading
                  }
                  onChange={(val) => {
                    fp.setFieldTouched("disposition", true, false);
                    fp.setFieldValue("disposition", String(val));
                  }}
                  // validation error first priority, else API error
                  error={
                    (fp.touched.disposition && fp.errors.disposition
                      ? String(fp.errors.disposition)
                      : undefined) ||
                    dispositionsBox.error ||
                    undefined
                  }
                />

                {/* Description */}
                <Field name="description">
                  {(p: FieldProps) => (
                    <Box>
                      <FloatingInputField
                        {...p}
                        label="Description"
                        maxLength={500}
                        required
                      />
                      <HStack justify="space-between" mt={1}>
                        <Box />
                        <Text fontSize="xs" color="gray.500">
                          {fp.values.description.length}/500
                        </Text>
                      </HStack>
                    </Box>
                  )}
                </Field>

                <Button
                  type="submit"
                  h="48px"
                  rounded="lg"
                  loading={raise.loading || fp.isSubmitting}
                  disabled={!canSubmit}
                >
                  Register
                </Button>

                {/* Result */}
                {raise.error && (
                  <Box
                    p={3}
                    rounded="lg"
                    bg="red.50"
                    borderWidth="1px"
                    borderColor="red.100"
                  >
                    <Text fontWeight="semibold" color="red.700">
                      {raise.error}
                    </Text>
                    {/* {raise.statusCode && (
                      <Text fontSize="sm" color="red.600" mt={1}>
                        StatusCode: {raise.statusCode}
                      </Text>
                    )} */}
                  </Box>
                )}

                {raise.data && (
                  <Box
                    p={4}
                    rounded="xl"
                    bg="green.50"
                    borderWidth="1px"
                    borderColor="green.100"
                  >
                    <Text fontWeight="bold" color="green.800">
                      Complaint Registered
                    </Text>

                    <VStack align="stretch" mt={3} gap={2}>
                      <Row label="Ticket ID" value={raise.data.ticketId} />
                      <Row
                        label="Ticket Status"
                        value={raise.data.ticketStatus}
                      />
                      <Row label="Reason" value={raise.data.responseReason} />
                      <Row label="Ticket Type" value={raise.data.ticketType} />
                      {raise.data.assigned && (
                        <Row label="Assigned" value={raise.data.assigned} />
                      )}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Form>
          );
        }}
      </Formik>
    </BbpsComplainLayout>
  );
};

export default RaiseTxnComplaintPage;
